/**
 * Map Store
 * Manages map-related state
 */
import { create } from 'zustand';
import { debugLog, exposeLayerStatus } from '@/services/map/mapDebug';
import type { LayerStatus } from '@/services/map/mapReadiness';

interface Polygon {
  polygon: string;
  [key: string]: unknown;
}

type MapToastTone = 'default' | 'success' | 'error';

interface MapStore {
  mapLoading: boolean;
  initialLoadComplete: boolean;
  selectedPolygon: Polygon | null;
  showRegionModal: boolean;
  /**
   * Per-layer status map for loading hints & error tracking.
   * Keys are simple identifiers like "vessels", "incidents", "ports".
   */
  layerStatus: Record<string, { status: LayerStatus; errorMessage?: string | null }>;
  toastMessage: string | null;
  toastTone: MapToastTone;

  // Actions
  setMapLoading: (loading: boolean) => void;
  setInitialLoadComplete: (complete: boolean) => void;
  setSelectedPolygon: (polygon: Polygon | null) => void;
  setShowRegionModal: (show: boolean) => void;
  setLayerStatus: (layerKey: string, status: LayerStatus, errorMessage?: string | null) => void;
  resetLayerStatus: (layerKey?: string) => void;
  showToast: (message: string, tone?: MapToastTone, durationMs?: number) => void;
  clearToast: () => void;
}

let toastTimeoutId: number | null = null;

export const useMapStore = create<MapStore>((set) => ({
  mapLoading: true,
  initialLoadComplete: false,
  selectedPolygon: null,
  showRegionModal: false,
  layerStatus: {},
  toastMessage: null,
  toastTone: 'default' as MapToastTone,

  setMapLoading: (loading) => set({ mapLoading: loading }),
  setInitialLoadComplete: (complete) => set({ initialLoadComplete: complete }),
  setSelectedPolygon: (polygon) => set({ selectedPolygon: polygon }),
  setShowRegionModal: (show) => set({ showRegionModal: show }),
  setLayerStatus: (layerKey, status, errorMessage) =>
    set((state) => {
      const next = {
        ...state.layerStatus,
        [layerKey]: {
          status,
          errorMessage: errorMessage ?? null,
        },
      };
      // Expose to window for debugging
      exposeLayerStatus(next);
      debugLog('MapStore', `layer ${layerKey} -> ${status}`, errorMessage || '');
      return { layerStatus: next };
    }),
  showToast: (message, tone = 'default', durationMs = 4000) => {
    if (toastTimeoutId) {
      window.clearTimeout(toastTimeoutId);
      toastTimeoutId = null;
    }
    set({ toastMessage: message, toastTone: tone });
    toastTimeoutId = window.setTimeout(() => {
      set({ toastMessage: null });
      toastTimeoutId = null;
    }, durationMs);
  },
  clearToast: () => {
    if (toastTimeoutId) {
      window.clearTimeout(toastTimeoutId);
      toastTimeoutId = null;
    }
    set({ toastMessage: null });
  },
  resetLayerStatus: (layerKey) =>
    set((state) => {
      if (!layerKey) {
        debugLog('MapStore', 'resetting all layer statuses');
        exposeLayerStatus({});
        return { layerStatus: {} };
      }
      const next = { ...state.layerStatus };
      delete next[layerKey];
      exposeLayerStatus(next);
      debugLog('MapStore', `reset layer status: ${layerKey}`);
      return { layerStatus: next };
    }),
}));
